/**
 * React Query client configuration.
 *
 * Creates and configures a QueryClient instance with environment-specific
 * options for optimal performance and debugging in different environments.
 *
 * @module queryClient
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Creates a configured QueryClient instance with environment-specific options.
 *
 * @returns A configured QueryClient instance
 */
export function createQueryClient(): QueryClient {
  const isDevelopment = import.meta.env.DEV;

  return new QueryClient({
    defaultOptions: {
      queries: {
        // Retry failed requests once
        retry: 1,
        // Don't refetch on window focus to reduce unnecessary network requests
        refetchOnWindowFocus: false,
        // Environment-specific stale time
        // Development: Longer stale time for better debugging experience
        // Production: Shorter stale time for fresher data
        staleTime: isDevelopment ? 1000 * 60 * 5 : 1000 * 60 * 2, // 5 min dev, 2 min prod
        // Cache time (now called gcTime in v5, but keeping for compatibility)
        gcTime: isDevelopment ? 1000 * 60 * 10 : 1000 * 60 * 5, // 10 min dev, 5 min prod
      },
      mutations: {
        // Retry failed mutations once
        retry: 1,
      },
    },
  });
}

